/**
 * Wire contract for the Modeling screen — the cohort-analytics read that backs the
 * KPI row, the credit-loss curve, and the prepayment curves for a slice of the
 * portfolio.
 *
 * ONE server read (`GET /api/modeling/curves`) runs the cohort aggregation in
 * Postgres for the active segment, then the light pure engine in the worker, and
 * returns a compact per-age curve dataset. Pricing + cash-flow run client-side off
 * this read (the engine lives in `common/`, so it is client-safe), so there is no
 * pricing/cash-flow endpoint here.
 *
 * Segment encoding is IDENTICAL to the Portfolio read: the active portfolio segment
 * rides the query string as `segment` = base64url(JSON(Segment)), optional (absent =
 * the whole portfolio). The same store drives both screens, the same `buildSegmentWhere`
 * decodes it server-side, so the two reads cannot drift on what "the cohort" means.
 *
 * Unlike Portfolio's inventory rows (Decimal money carried as strings to preserve
 * ledger precision), every number here is a COMPUTED analytic — curve values, hazards,
 * exposures, balance-weighted terms, currency rollups — so they cross the wire as
 * plain `number`s. The response schemas mirror the engine result interfaces in
 * `common/modeling/types.ts` exactly; keep them in lockstep with that source of truth.
 *
 * The response is resilient to an EMPTY cohort: every curve/hazard array may be empty
 * and every summary count may be zero, so the screen renders an honest empty state
 * rather than the read 500-ing.
 */
import { z } from 'zod';
import type { ApiContract } from '../contracts';

// ── Curve primitives (mirror common/modeling/types.ts) ───────────────────────

/** One discrete per-age hazard (life-table rate) plus its exposure denominator. */
export const HazardPointSchema = z.object({
  ageMonths: z.number(),
  hazard: z.number(),
  exposure: z.number(),
  observed: z.boolean(),
});
export type HazardPoint = z.infer<typeof HazardPointSchema>;

/** One point on a plotted curve: the value, its confidence band, and provenance. */
export const CurvePointSchema = z.object({
  ageMonths: z.number(),
  /** Meaning depends on the curve (cum loss %, remaining balance %, smm, cpr, ...). */
  value: z.number(),
  /** Confidence band low (clamped). */
  lower: z.number(),
  /** Confidence band high (clamped). */
  upper: z.number(),
  /** n(a) for the background exposure bars. */
  exposure: z.number(),
  /** true = solid/observed, false = dashed/extrapolated. */
  observed: z.boolean(),
  /** F180: the SEMANTICS of [lower, upper] — 'statistical' (observed ~95% CI),
   *  'projected' (the extrapolated heuristic fan), or 'none' (collapsed band). Optional:
   *  banded curves set it, flat curves may omit it. The chart layer turns it into label
   *  copy so the projected fan is never labelled with the observed "~95%" statistic. */
  bandKind: z.enum(['statistical', 'projected', 'none']).optional(),
});
export type CurvePoint = z.infer<typeof CurvePointSchema>;

// ── Engine results (mirror common/modeling/types.ts) ─────────────────────────

/** Cohort-level rollup: counts, balances, and balance-weighted contractual terms. */
export const CohortSummarySchema = z.object({
  loanCount: z.number(),
  /** Loans with a real AGE ANCHOR — a provided origination date, or one estimated from a
   *  first appearance strictly AFTER the org's earliest observed month. Unanchored
   *  earliest-month loans (v9 / R2) are NOT ageable. */
  ageableLoanCount: z.number(),
  /** Loans with NO origination anchor of any kind (no provided date and no snapshot to
   *  estimate from). */
  excludedNoOriginationDate: z.number(),
  /** v9 (WS-F / R2): loans first seen in the org's EARLIEST observed month with no provided
   *  origination date — new-vs-seasoned indistinguishable, so they are excluded from the
   *  age-indexed curves, counted here, and nudged (an origination-date file unlocks them). */
  excludedUnanchoredFirstMonth: z.number(),
  /** Of the unanchored loans, how many an origination-date file ALONE fully unlocks (they
   *  already carry a usable term). */
  unanchoredUnlockableCount: z.number(),
  /** Loans driving the curves: ageable AND with a term (direct or derived from maturity). */
  modeledLoanCount: z.number(),
  /** Ageable loans dropped for want of a term (direct or derivable from maturity). */
  excludedNoTerm: z.number(),
  /** Of the modeled loans, how many got their term from (maturity_date − origination_date). */
  derivedTermLoanCount: z.number(),
  totalCurrentBalance: z.number(),
  totalOriginalBalance: z.number(),
  /** Balance-weighted effective term (months) over the modeled loans. */
  weightedAvgTermMonths: z.number(),
  /** Balance-weighted note rate (PERCENT points). */
  weightedAvgNoteRatePct: z.number(),
  /** Min effective term (months) across the modeled loans (0 when none). */
  minTermMonths: z.number(),
  /** Max effective term (months) across the modeled loans (0 when none). */
  maxTermMonths: z.number(),
  /** Balance-weighted standard deviation of the effective term (months). */
  termStdDevMonths: z.number(),
  maxObservedAgeMonths: z.number(),
  /** Of the modeled loans, how many were aged from an ESTIMATED origination (first-seen fallback,
   *  no origination_date provided) — surfaced so the analyst sees how much aging rests on the estimate. */
  estimatedOriginationLoanCount: z.number(),
  /** How many loans were booked as a full payoff purely because they STOPPED reporting before the
   *  portfolio as-of (implicit-early-payoff synthesis) — so a CPR/expected-life reader can see how
   *  much measured prepayment is inferred vs. reported. */
  impliedPayoffLoanCount: z.number(),
  prepayExcludedNoScheduleBasis: z.number(),
  /** Modeled SEASONED ENTRANTS (v7): provided origination predates the portfolio's first
   *  observed month, so their pre-observation months are excluded from the curves
   *  (left-truncation) — surfaced as a data note. */
  seasonedEntrantLoanCount: z.number(),
  /** v8 (WS-B P3): loans with an origination anchor + term but NO snapshot observation
   *  (origination extract) — excluded from the modeled population and counted. */
  excludedNoSnapshot: z.number(),
  /** Of excludedNoSnapshot, loans carrying a transaction-file loss/payoff event with no age
   *  axis to book on — surfaced so the event is never silently dropped. */
  excludedNoSnapshotWithEvents: z.number(),
  /** v8 (WS-B P3/F175): modeled loans excluded from the LOSS curve (flag/status-only
   *  charge-off, no derivable magnitude) — modeled for survival, out of the loss denominator. */
  lossExcludedFlagOnly: z.number(),
  /** v8 (WS-B P4): the cohort's earliest loss-observable age (min per-loan floor over the
   *  loss-denominator loans) — feeds the WARM annualization span. */
  firstObservableAgeMonths: z.number(),
  /** Speed-pool ruling 2026-07-17 (renamed from prepayExcludedNoMethodCount): modeled loans
   *  WITH observed scheduled principal but NO partial-reporting channel (no direct amount,
   *  no actual-vs-sched) — IN the measure (payoff detection is universal; an un-paid-off
   *  loan is a measured zero) with partial prepayment structurally invisible: their speeds
   *  reflect full payoffs only. Disclosure + nudge, not an exclusion. */
  prepayPartialUnreportedCount: z.number(),
  /** v8 (core-A, RB-4): modeled loans with NO observed scheduled principal (due/paid) —
   *  no speed denominator basis (no reconstruction), excluded from the measure entirely. */
  prepayExcludedNoScheduleCount: z.number(),
  /** v8 (core-A, RB-3): loan-months whose reported prepayment amount fell on an unmeasurable
   *  month — dropped from the measure (never booked, never spread), disclosed here. */
  prepayDroppedUnmeasurableMonths: z.number(),
  /** Founder-directed disclosure (2026-07-17): modeled loans showing ACTIVITY after their
   *  terminal charge-off (≥1 post-terminal row carrying a present value — balance, payment,
   *  prepayment, payoff, or further charge-off/recovery amount). Their later months are
   *  excluded from the curves; if the book's loans can survive a partial charge-off, that is
   *  a declared fact the customer should tell us (Phase-3 rail). */
  postChargeOffActivityCount: z.number(),
  /** F174 (WS-G / R5): the SPEED POOL — modeled loans with an observed scheduled-principal
   *  basis (`hasObservedSchedule`), the population the prepayment-speed measure runs over. 0 ⇒
   *  no CPR/expected-life basis at all, so the UI renders "—" (a no-data gate mirroring
   *  `lossInputsPresent`), never a confident 0.00% CPR. OPTIONAL: the store always emits it,
   *  pre-existing fixtures omit it (consumers read `?? 0`); absent ⇒ "not gated" (no regression). */
  speedPoolLoanCount: z.number().optional(),
});
export type CohortSummary = z.infer<typeof CohortSummarySchema>;

/** Output of the vintage credit-loss engine (or its WARM fallback). */
export const CreditLossResultSchema = z.object({
  marginalHazard: z.array(HazardPointSchema),
  /** Cumulative loss as a fraction of ORIGINAL balance, by age. */
  cumulativeLossCurve: z.array(CurvePointSchema),
  /**
   * CONDITIONAL monthly default rate by age (decimal): defaultedPrincipal / begin
   * balance, exposure-weighted, smoothed + extrapolated to term — the per-period MDR
   * the client cash-flow projection consumes. Distinct from `marginalHazard` (the
   * %-of-original vintage rate); this keeps projected losses consistent with the
   * displayed loss curve. Mirrors `prepayment.smmByAge`.
   */
  defaultHazardByAge: z.array(HazardPointSchema),
  /**
   * CONDITIONAL monthly NET-LOSS rate by age (decimal): max(0, netChargeOff) / begin
   * balance — the same conditional construction as defaultHazardByAge but with the NET
   * charge-off (recoveries netted) as the numerator. The client cash-flow projection runs
   * the balance off at the gross `defaultHazardByAge` but accrues LOSS DOLLARS at this net
   * rate (F85/F111), so projected loss matches the observed vintage curve — no assumed LGD.
   * net == gross when a book reports only gross charge-offs. OPTIONAL: the engine always
   * emits it, pre-existing fixtures omit it (consumers read `?? []`).
   */
  netLossHazardByAge: z.array(HazardPointSchema).optional(),
  /** Headline = cumulative loss at term (PERCENT). */
  expectedLifetimeLossPct: z.number(),
  termMonths: z.number(),
  lastObservedAgeMonths: z.number(),
  method: z.enum(['vintage', 'warm']),
});
export type CreditLossResult = z.infer<typeof CreditLossResultSchema>;

/** Output of the prepayment engine: SMM/CPR hazards, survival curves, and life. */
export const PrepaymentResultSchema = z.object({
  smmByAge: z.array(HazardPointSchema),
  cprByAge: z.array(HazardPointSchema),
  /**
   * The smoothed + gap-bridged + decay-extrapolated SMM path (ages 0..term) that
   * drives the behavioral survival curve and the headline expected life — served so
   * client-side what-ifs (the Pricing panel's prepayment-adjusted term via
   * `expectedLifeForTerm`) reproduce the displayed life exactly. Distinct from
   * `smmByAge`, the RAW observed hazards.
   */
  extendedSmmByAge: z.array(HazardPointSchema),
  /** Behavioral remaining-balance %, by age. */
  survivalCurve: z.array(CurvePointSchema),
  /** Contractual remaining-balance %, by age (no bands needed; lower = upper = value). */
  contractualSurvivalCurve: z.array(CurvePointSchema),
  cumulativeEarlyPrincipalPct: z.array(CurvePointSchema),
  /** Behavioral WAL. */
  expectedLifeYears: z.number(),
  /** Contractual WAL. */
  contractualLifeYears: z.number(),
  /** Headline (PERCENT). */
  avgAnnualizedCprPct: z.number(),
  lastObservedAgeMonths: z.number(),
});
export type PrepaymentResult = z.infer<typeof PrepaymentResultSchema>;

/** The KPI-row headline numbers assembled from the credit-loss + prepayment results. */
export const ModelingHeadlineSchema = z.object({
  expectedLifetimeLossPct: z.number(),
  expectedLifeYears: z.number(),
  contractualLifeYears: z.number(),
  avgAnnualizedCprPct: z.number(),
  loansUsed: z.number(),
  totalCurrentBalance: z.number(),
  totalOriginalBalance: z.number(),
});
export type ModelingHeadline = z.infer<typeof ModelingHeadlineSchema>;

// ── Dataset freshness (block-until-settled) ──────────────────────────────────

/**
 * Freshness of the org's stored modeling dataset, from the screens' point of view (plan §9-SR-U /
 * §5-C7). Drives the serve-vs-block UX: the whole-portfolio Modeling view serves the last-complete
 * dataset whenever one exists (plain when `fresh`, or last-good-while-refreshing when `updating`),
 * and hard-blocks behind an honest "still processing (N remaining)" state only when there is nothing
 * good to serve yet.
 *
 *  - `fresh`    — the stored dataset faithfully reflects the latest data (buckets computed + not
 *    stale, dirty queue drained, backfill complete, derivation version current). Safe to show.
 *  - `updating` — a derive/refresh is in flight BUT a last-complete D4 dataset already exists (the
 *    store keeps it through the refresh via an atomic swap). The whole-portfolio view serves that
 *    last-good dataset with an `asOf` timestamp + an "updating — N of M loans" indicator; a CUSTOM
 *    segment still hard-blocks (its D1 inputs are genuinely in flux mid-derive). `loansRemaining`
 *    counts what is left; `totalLoanCount` / `derivedLoanCount` drive the N-of-M indicator.
 *  - `deriving` — loans are being (re)derived AND no last-complete dataset exists yet (the very
 *    first backfill) → hard block; `loansRemaining` counts what is left.
 *  - `stale`    — settled but a D4 refresh is pending AND no last-complete dataset exists yet
 *    (a just-backfilled org marked stale before its first compute) → hard block. Transient.
 *
 * Clients must tolerate an unknown value (treat it as "not servable" → block) so a future status
 * can ship additively.
 */
export const ModelingDatasetStatusSchema = z.enum(['fresh', 'updating', 'deriving', 'stale']);
export type ModelingDatasetStatusWire = z.infer<typeof ModelingDatasetStatusSchema>;

/**
 * The serve-vs-block status payload — returned standalone from `GET /api/modeling/status` (the
 * cheap poll) AND embedded in the curves response so a single read carries both the numbers and
 * their freshness.
 */
export const ModelingDatasetStatusPayloadSchema = z.object({
  datasetStatus: ModelingDatasetStatusSchema,
  /** Loans still being (re)derived (dirty + missing-facts, or — during a version-bump re-derive —
   *  loans whose stored derivation version is behind the current). 0 when `fresh`. */
  loansRemaining: z.number(),
  /** When the current (last-complete) D4 buckets were computed, ISO 8601 UTC (null if never). The
   *  as-of the whole-portfolio view serves while `updating` — "updated as of {asOf}". */
  bucketsComputedAt: z.string().datetime().nullable(),
  /** M in the "N of M loans" progress indicator: the org's total loan count (O(1) counter). */
  totalLoanCount: z.number(),
  /** N in the "N of M loans" progress indicator: loans that already have their derived facts
   *  (O(1) counter). While `updating`, `M − N` approximates the loans still to (re)derive. */
  derivedLoanCount: z.number(),
  /**
   * WS-C P6 (F74/F42): loans currently EXCLUDED from the served curves because a PROCESSING
   * failure quarantined them (a poisoned derive range or a poisoned recompute loan). A
   * PROCESSING-exclusion disclosure — distinct from the honest, permanent BUSINESS exclusions
   * (no-snapshot / no-term / no-origination): these loans are expected to return once the
   * failure is investigated. The read path excludes exactly this set from every served
   * aggregate, so the disclosed count and the numbers always agree. 0 in the healthy case.
   */
  quarantinedExcludedCount: z.number(),
});
export type ModelingDatasetStatusPayload = z.infer<typeof ModelingDatasetStatusPayloadSchema>;

// ── GET /api/modeling/curves ─────────────────────────────────────────────────

export const ModelingCurvesQuerySchema = z.object({
  /**
   * The active portfolio segment, base64url(JSON(Segment)) — the SAME encoding the
   * Portfolio loans read uses (`portfolio.ts`). Optional: absent means the whole
   * portfolio. Rides the query string (segments are small) so the read stays a
   * cacheable GET; the server decodes + normalizes it and feeds it to the same
   * `buildSegmentWhere` cohort predicate.
   */
  segment: z.string().optional(),
});
export type ModelingCurvesQuery = z.infer<typeof ModelingCurvesQuerySchema>;

// ── Charge-off survivorship (v9 partial-CO declared fact) ────────────────────

/**
 * The survivorship declared-fact state the curves read carries (and the PUT below
 * writes). `survivorship` is the EFFECTIVE answer (the stored declaration, else the
 * prefilled 'leaves' default); `provenance` mirrors the RB-6 vocabulary ('suggested'
 * until the customer explicitly answers — then 'user_declared', sticky);
 * `declared` = a stored row exists (keeps the control revisable after the evidence
 * count drops to 0); `hasChargeOffVocabulary` = the org's status classifications
 * carry a `charged_off` meaning — the vocabulary then governs the modeling semantics
 * and the question is NOT asked.
 */
export const ChargeOffSurvivorshipStateSchema = z.object({
  survivorship: z.enum(['leaves', 'survives']),
  provenance: z.enum(['suggested', 'user_declared']),
  declared: z.boolean(),
  hasChargeOffVocabulary: z.boolean(),
});
export type ChargeOffSurvivorshipStateResponse = z.infer<typeof ChargeOffSurvivorshipStateSchema>;

/**
 * The full Modeling read: the KPI headline, the credit-loss engine result, the prepayment engine
 * result, the cohort summary, AND the serve-vs-block status so a single read carries both the
 * numbers and their freshness. Every curve array may be empty (an empty cohort still produces a
 * well-formed, render-able response).
 *
 * When `datasetStatus === 'updating'` the numbers are the LAST-COMPLETE whole-portfolio dataset
 * (served while a refresh is in flight); `asOf` is the timestamp they reflect and
 * `totalLoanCount`/`derivedLoanCount` drive the "updating — N of M loans" indicator. When the
 * server WITHHELD (a not-servable state, or a custom segment mid-derive) the curves are empty and
 * `asOf` is null.
 */
export const ModelingCurvesResponseSchema = z.object({
  headline: ModelingHeadlineSchema,
  creditLoss: CreditLossResultSchema,
  prepayment: PrepaymentResultSchema,
  summary: CohortSummarySchema,
  /** Freshness of the stored dataset these numbers came from (`fresh` served live; `updating`
   *  served last-good; `deriving`/`stale` withheld → the screen blocks). */
  datasetStatus: ModelingDatasetStatusSchema,
  /** When the served numbers were last complete, ISO 8601 UTC — the as-of shown while `updating`
   *  ("updated as of {asOf}"). Non-null only when real numbers are served (`fresh`/`updating`). */
  asOf: z.string().datetime().nullable(),
  /** M in "N of M loans" — the org's total loan count. Drives the `updating` progress indicator. */
  totalLoanCount: z.number(),
  /** N in "N of M loans" — loans that already have their derived facts. */
  derivedLoanCount: z.number(),
  /** WS-C P6: loans temporarily excluded from these curves for a processing failure (see
   *  {@link ModelingDatasetStatusPayloadSchema}.quarantinedExcludedCount — same disclosure,
   *  embedded here so a single curves read carries the numbers AND what they exclude). */
  quarantinedExcludedCount: z.number(),
  /**
   * Whether ANY accepted, included mapping in this organization supplies a
   * loss-magnitude field (`net_charge_off_amount` / `charge_off_amount`).
   * When false, the loss curve is structurally flat at 0% — the UI must present
   * "no charge-off data" instead of a confident 0% lifetime loss, so an absent
   * loss feed is never mistaken for a genuinely loss-free book.
   */
  lossInputsPresent: z.boolean(),
  /**
   * v9 (partial-CO): the charge-off survivorship declared-fact state, riding the one
   * curves read (an indexed registry read + one EXISTS — no extra round trip, no
   * org-row scan). Drives the evidence-triggered question on the
   * `postChargeOffActivity` note: shown when the count is non-zero AND the org has no
   * charged_off status vocabulary (the vocabulary governs when present), and kept
   * visible once DECLARED so the answer stays revisable after the count drops to 0.
   */
  chargeOffSurvivorship: ChargeOffSurvivorshipStateSchema,
  /**
   * F183 (WS-G / R5): the DATA as-of — the reference-snapshot MONTH the curves are
   * right-censored to, as an ISO month string `"YYYY-MM"` (derived from
   * `portfolio_modeling_state.referenceOrdinal`). DISTINCT from `asOf` (= the D4 recompute
   * time): this is the latest data the numbers reflect, shown on the always-visible
   * Data-Basis Statement ("Measured against snapshots through {referenceSnapshotDate}"). Null
   * when the org has no modeled loans (or the read was withheld). OPTIONAL/additive — clients
   * tolerate absence (WS-G Risks §); the handler always sets it on a served response.
   */
  referenceSnapshotDate: z.string().nullable().optional(),
  /**
   * F99 (WS-G / R5): FALSE when a NON-EMPTY segment token was sent but could not be applied
   * (it decoded malformed), so the server WITHHELD the segment's curves rather than serving
   * whole-portfolio numbers under the client's chips. TRUE (or absent) means the segment was
   * applied — or none was sent. OPTIONAL/additive; consumers treat absent as TRUE
   * (`segmentApplied !== false`). NOTE: the malformed→false path is fed by lane G3's
   * `decodeSegmentParam` discriminated result; until that lands the handler defaults it TRUE.
   */
  segmentApplied: z.boolean().optional(),
});
export type ModelingCurvesResponse = z.infer<typeof ModelingCurvesResponseSchema>;

export const modelingCurvesContract = {
  method: 'GET',
  path: '/api/modeling/curves',
  query: ModelingCurvesQuerySchema,
  response: ModelingCurvesResponseSchema,
} as const satisfies ApiContract;

// ── GET /api/modeling/status ─────────────────────────────────────────────────

/**
 * The cheap block-until-settled poll: `GET /api/modeling/status` returns just the freshness of the
 * org's stored modeling dataset, WITHOUT running the curve engine. The screens poll it (4s while
 * not fresh) to decide whether to block, and gate the heavier curves read on `fresh`. Response is
 * the shared {@link ModelingDatasetStatusPayloadSchema}.
 */
export const ModelingStatusResponseSchema = ModelingDatasetStatusPayloadSchema;
export type ModelingStatusResponse = z.infer<typeof ModelingStatusResponseSchema>;

export const modelingStatusContract = {
  method: 'GET',
  path: '/api/modeling/status',
  response: ModelingStatusResponseSchema,
} as const satisfies ApiContract;

// ── PUT /api/modeling/charge-off-survivorship ────────────────────────────────

/**
 * Declare the charge-off survivorship fact (the evidence-triggered question — "when a
 * loan is charged off, does it leave your exports, or can it survive a partial
 * write-down?"). Always persisted `user_declared` (the customer answered; sticky).
 * When the answer CHANGES the derive's activation gate (no charged_off vocabulary and
 * the effective answer flipped), the org's modeling facts are marked stale and the
 * derive-only re-derive is kicked — `rederiveTriggered` reports it so the client can
 * flip into the processing block. NEVER blocks anything: an unanswered book simply
 * keeps the 'leaves' default.
 */
export const ChargeOffSurvivorshipDeclareRequestSchema = z.object({
  survivorship: z.enum(['leaves', 'survives']),
});
export type ChargeOffSurvivorshipDeclareRequest = z.infer<
  typeof ChargeOffSurvivorshipDeclareRequestSchema
>;

export const ChargeOffSurvivorshipDeclareResponseSchema = z.object({
  state: ChargeOffSurvivorshipStateSchema,
  /** True when the answer changed the modeling semantics and the org re-derive was kicked. */
  rederiveTriggered: z.boolean(),
});
export type ChargeOffSurvivorshipDeclareResponse = z.infer<
  typeof ChargeOffSurvivorshipDeclareResponseSchema
>;

export const modelingChargeOffSurvivorshipContract = {
  method: 'PUT',
  path: '/api/modeling/charge-off-survivorship',
  body: ChargeOffSurvivorshipDeclareRequestSchema,
  response: ChargeOffSurvivorshipDeclareResponseSchema,
} as const satisfies ApiContract;

// ── POST /api/modeling/ftp-curve ─────────────────────────────────────────────

/**
 * One parsed funding (FTP) curve point: a term in months and its rate in PERCENT
 * points. Mirrors the `FundingCurvePoint` engine interface in
 * `common/modeling/types.ts` exactly — kept as a Zod schema here so the parse read
 * (which crosses the wire) is both-side validatable; the inferred type stays
 * structurally compatible with `FundingCurvePoint`.
 */
export const FundingCurvePointSchema = z.object({
  termMonths: z.number(),
  ratePct: z.number(),
});
export type FundingCurvePointWire = z.infer<typeof FundingCurvePointSchema>;

/**
 * Request to parse an uploaded FTP-curve CSV. The whole (tiny) file is sent as
 * text — the server identifies the term + rate columns, the header row, and the
 * rate format (preferring Claude, falling back to a deterministic resolver), and
 * returns the points normalized to PERCENT.
 */
export const ParseFundingCurveRequestSchema = z.object({
  csvText: z.string(),
});
export type ParseFundingCurveRequest = z.infer<typeof ParseFundingCurveRequestSchema>;

/**
 * Parse result. `status` is `ok` when both columns were found and yielded points;
 * `columns-not-found` when the term/rate columns could not be identified; `empty`
 * when the file had no rows. `source` reports whether the AI or the deterministic
 * fallback produced the answer (so the client/UI can be honest about provenance).
 */
export const ParseFundingCurveResponseSchema = z.object({
  status: z.enum(['ok', 'columns-not-found', 'empty']),
  points: z.array(FundingCurvePointSchema),
  source: z.enum(['ai', 'fallback']),
});
export type ParseFundingCurveResponse = z.infer<typeof ParseFundingCurveResponseSchema>;

export const parseFundingCurveContract = {
  method: 'POST',
  path: '/api/modeling/ftp-curve',
  body: ParseFundingCurveRequestSchema,
  response: ParseFundingCurveResponseSchema,
} as const satisfies ApiContract;

// ── GET/PUT /api/modeling/assumptions (per-user draft, cross-device) ──────────

/**
 * Entry mode for a convertible dollar assumption: 'usd' enters dollars, 'pct'
 * enters a percent of the loan amount. The field's VALUE is stored in the units
 * of its current mode (see `features/modeling/amount-percent.ts`).
 */
export const AmountEntryModeSchema = z.enum(['usd', 'pct']);
export type AmountEntryMode = z.infer<typeof AmountEntryModeSchema>;

/**
 * One user-added pricing build-up line item. `basis` mirrors the engine's
 * `PricingCustomBasis` ('pctAnnual' | 'usdOneTime' | 'usdAnnual' | 'pctOneTime') —
 * kept as a Zod enum here so the persisted draft is both-side validatable.
 */
export const ModelingCustomLineItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** Direction, implied by which form section it was added in (costs add, fees subtract). */
  kind: z.enum(['add', 'subtract']),
  basis: z.enum(['pctAnnual', 'usdOneTime', 'usdAnnual', 'pctOneTime']),
  /** In the basis's units: $ one-time, $ / year, PERCENT points / year, or one-time PERCENT points. */
  value: z.number(),
});
export type ModelingCustomLineItem = z.infer<typeof ModelingCustomLineItemSchema>;

/**
 * The persisted Modeling pricing/cash-flow assumptions — the full `ModelingInputs`
 * shape the Pricing + Cash-Flow panels consume (the client store's source of truth
 * derives its type from THIS schema, so the wire and the store cannot drift).
 * Percent fields are PERCENT points; dollar fields are in the units of their entry
 * mode. `fundingCurve` empty means NOT PROVIDED — there is deliberately no default
 * curve, and the panels gate their outputs until the user supplies one.
 */
export const ModelingInputsSchema = z.object({
  /** Representative new-loan amount ($); null until seeded from the cohort or entered. */
  loanAmount: z.number().nullable(),
  /** Optional proposed note rate (PERCENT points) — drives the underpricing delta. */
  proposedNoteRatePct: z.number().nullable(),
  /** Target spread over matched-maturity FTP (PERCENT points). */
  targetSpreadPct: z.number(),
  /** One-time origination cost — in the units of `originationCostMode`. */
  originationCost: z.number(),
  /** Annual servicing cost — in the units of `annualServicingCostMode`. */
  annualServicingCost: z.number(),
  /** Contractual term (months). */
  contractualTermMonths: z.number(),
  /** One-time upfront fees (revenue credit) — in the units of `upfrontFeesMode`. */
  upfrontFees: z.number(),
  /** Annual recurring fees (revenue credit) — in the units of `annualRecurringFeesMode`. */
  annualRecurringFees: z.number(),
  originationCostMode: AmountEntryModeSchema,
  annualServicingCostMode: AmountEntryModeSchema,
  upfrontFeesMode: AmountEntryModeSchema,
  annualRecurringFeesMode: AmountEntryModeSchema,
  /** User-added pricing build-up line items (costs add, fees subtract). */
  customLineItems: z.array(ModelingCustomLineItemSchema).readonly(),
  /** Funding (FTP) curve: term (months) → rate (PERCENT points). EMPTY = not provided. */
  fundingCurve: z.array(FundingCurvePointSchema).readonly(),
  /** Loss given default (PERCENT points). */
  lgdPct: z.number(),
  /** Whether the optional RAROC capital-charge path is enabled. */
  rarocEnabled: z.boolean(),
  /** RAROC: capital allocation (PERCENT points, e.g. 9). */
  capitalAllocationPct: z.number(),
  /** RAROC: target pre-tax ROE (PERCENT points, e.g. 15). */
  targetPretaxRoePct: z.number(),
});
export type ModelingInputs = z.infer<typeof ModelingInputsSchema>;

/**
 * Schema version stamped on the persisted per-user assumptions row. Bump when
 * {@link ModelingInputsSchema} changes shape incompatibly; the GET handler can then
 * migrate (or discard) older payloads keyed off the stored version.
 */
export const MODELING_ASSUMPTIONS_VERSION = 1;

/** The user's saved assumptions, or null when they have none saved yet (the client
 *  keeps its defaults — including the empty, not-provided funding curve). */
export const ModelingAssumptionsResponseSchema = z.object({
  assumptions: ModelingInputsSchema.nullable(),
});
export type ModelingAssumptionsResponse = z.infer<typeof ModelingAssumptionsResponseSchema>;

export const modelingAssumptionsContract = {
  method: 'GET',
  path: '/api/modeling/assumptions',
  response: ModelingAssumptionsResponseSchema,
} as const satisfies ApiContract;

export const ModelingAssumptionsUpsertRequestSchema = z.object({
  assumptions: ModelingInputsSchema,
});

export const modelingAssumptionsUpsertContract = {
  method: 'PUT',
  path: '/api/modeling/assumptions',
  body: ModelingAssumptionsUpsertRequestSchema,
  response: ModelingAssumptionsResponseSchema,
} as const satisfies ApiContract;
